import React from 'react'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

// A membership row prices the activity either with a fixed euro price or with a
// percentage discount off the activity's own price -- never both, so switching the
// type clears the other value. A membership may appear at most once, which is
// enforced by hiding memberships already taken by the other rows.

export const PRICETYPE_FIXED = 'fixed'
export const PRICETYPE_DISCOUNT = 'discount'

export const membershipLabel = (membership) => {
  if (!membership) return ''
  const name = membership.displayName || membership.name || membership.id
  return name + (membership.state === 'deleted' ? ' (poistettu)' : '')
}

// A row saved before pricetype existed (the event ticket shape) is a fixed price.
export const rowPriceType = (row) => (row && row.pricetype ? row.pricetype : PRICETYPE_FIXED)

// Price the activity ends up at for this membership, or null when it cannot be resolved.
export const effectiveMembershipPrice = (row, activityPrice) => {
  if (!row) return null
  if (rowPriceType(row) === PRICETYPE_DISCOUNT) {
    const base = Number(activityPrice)
    const discount = Number(row.discount)
    if (!isFinite(base) || !isFinite(discount)) return null
    return Math.round(base * (1 - discount / 100) * 100) / 100
  }
  const price = Number(row.price)
  return isFinite(price) ? price : null
}

const formatEuros = (value) => (isFinite(Number(value)) ? Number(value).toFixed(2).replace('.', ',') : '')

const parseNumber = (raw) => {
  const cleaned = String(raw).replace(',', '.').replace(/[^0-9.]/g, '')
  const parsed = parseFloat(cleaned)
  return isFinite(parsed) ? parsed : 0
}

function MembershipPricesEditor({ value, memberships, activityPrice, onChange }) {
  const rows = Array.isArray(value) ? value : []
  const options = Array.isArray(memberships) ? memberships : []
  // Keeps what the user is literally typing ("12," / "12.") so decimals survive the
  // round trip through a number; cleared on blur so the stored value takes over again.
  const [drafts, setDrafts] = React.useState({})

  const emit = (next) => onChange(next)

  const updateRow = (index, changes) => {
    emit(rows.map((row, i) => (i === index ? { ...row, ...changes } : row)))
  }

  const handleTypeChange = (index, pricetype) => {
    setDrafts((d) => ({ ...d, [index]: undefined }))
    // Only one of price/discount is ever stored, so drop the value the other type held.
    if (pricetype === PRICETYPE_DISCOUNT) {
      const { price, ...rest } = rows[index]
      emit(rows.map((row, i) => (i === index ? { ...rest, pricetype, discount: row.discount || 0 } : row)))
    } else {
      const { discount, ...rest } = rows[index]
      emit(rows.map((row, i) => (i === index ? { ...rest, pricetype, price: row.price || 0 } : row)))
    }
  }

  const handleAmountChange = (index, raw) => {
    setDrafts((d) => ({ ...d, [index]: raw }))
    const field = rowPriceType(rows[index]) === PRICETYPE_DISCOUNT ? 'discount' : 'price'
    updateRow(index, { [field]: parseNumber(raw) })
  }

  const handleAmountBlur = (index) => setDrafts((d) => ({ ...d, [index]: undefined }))

  const handleAdd = () => emit([...rows, { membership: '', pricetype: PRICETYPE_FIXED, price: 0 }])

  const handleRemove = (index) => {
    setDrafts({})
    emit(rows.filter((_, i) => i !== index))
  }

  // Memberships still selectable in this row: the unused ones plus whatever this row holds.
  const availableFor = (index) => {
    const taken = rows.filter((_, i) => i !== index).map((row) => row.membership)
    return options.filter((m) => !taken.includes(m.id) || m.id === rows[index].membership)
  }

  return (
    <Box>
      {rows.map((row, index) => {
        const type = rowPriceType(row)
        const isDiscount = type === PRICETYPE_DISCOUNT
        const stored = isDiscount ? row.discount : row.price
        const draft = drafts[index]
        const effective = effectiveMembershipPrice(row, activityPrice)
        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <TextField
              select
              size='small'
              label='Jäsenyys'
              value={row.membership || ''}
              onChange={(e) => updateRow(index, { membership: e.target.value })}
              sx={{ minWidth: 220 }}
            >
              {availableFor(index).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {membershipLabel(m)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size='small'
              label='Hinnoittelutapa'
              value={type}
              onChange={(e) => handleTypeChange(index, e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value={PRICETYPE_FIXED}>Kiinteä hinta</MenuItem>
              <MenuItem value={PRICETYPE_DISCOUNT}>Prosenttialennus</MenuItem>
            </TextField>
            <TextField
              size='small'
              label={isDiscount ? 'Alennus' : 'Hinta'}
              value={draft !== undefined ? draft : stored === undefined || stored === null ? '' : String(stored).replace('.', ',')}
              onChange={(e) => handleAmountChange(index, e.target.value)}
              onBlur={() => handleAmountBlur(index)}
              InputProps={{
                endAdornment: <InputAdornment position='end'>{isDiscount ? '%' : '€'}</InputAdornment>,
              }}
              sx={{ width: 140 }}
            />
            {isDiscount && effective !== null ? (
              <Typography variant='body2' color='text.secondary'>
                = {formatEuros(effective)} €
              </Typography>
            ) : null}
            <IconButton aria-label='Poista jäsenhinta' size='small' color='error' onClick={() => handleRemove(index)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Box>
        )
      })}
      <Button size='small' startIcon={<AddCircleOutlineIcon />} onClick={handleAdd}>
        Lisää jäsenhinta
      </Button>
      {options.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Jäsenyyksiä ei ole määritelty.
        </Typography>
      ) : null}
    </Box>
  )
}

export default MembershipPricesEditor
